import React from 'react';

// InvoicePreview — the "blueprint" Aria shows during Invoicing setup:
// "here's the blueprint I have, do you like it?". A clean, professional,
// print-style invoice (always light — invoices are white/printable) with
// sample data. Props let it fill real business/customer info later.

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function InvoicePreview({
  business = { name: 'Your Business', line2: 'Wichita, KS · (316) 555-0142', email: 'you@yourbiz.com' },
  customer = { name: 'Jordan Smith', line2: '1429 Oak St · Wichita, KS' },
  number = '1042',
  date = 'Jun 17, 2026',
  due = 'Net 7',
  items = [
    { desc: 'Full truckload junk removal', qty: 1, rate: 450 },
    { desc: 'Disposal & dump fees', qty: 1, rate: 60 },
    { desc: 'Labor — 2 crew, 1.5 hrs', qty: 3, rate: 45 },
  ],
  taxRate = 0,
} = {}) {
  const subtotal = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.rate) || 0), 0);
  const tax = subtotal * (taxRate || 0);
  const total = subtotal + tax;

  const th = { textAlign: 'left', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#8a8a92', padding: '0 0 6px' };
  const td = { fontSize: 12.5, color: '#1d1d1f', padding: '7px 0', borderTop: '1px solid #ececf0' };

  return (
    <div style={{ background: '#fff', color: '#1d1d1f', borderRadius: 14, padding: '18px 18px 16px', boxShadow: '0 8px 30px rgba(0,0,0,0.18)', border: '1px solid #ececf0', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.01em' }}>{business.name}</div>
          <div style={{ fontSize: 11.5, color: '#6e6e73', marginTop: 2 }}>{business.line2}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: '0.12em', color: '#00a3c4' }}>INVOICE</div>
          <div style={{ fontSize: 11.5, color: '#6e6e73', marginTop: 2 }}>#{number} · {date}</div>
        </div>
      </div>

      {/* Bill to */}
      <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#8a8a92' }}>Bill to</div>
          <div style={{ fontSize: 13, fontWeight: 600, marginTop: 3 }}>{customer.name}</div>
          <div style={{ fontSize: 11.5, color: '#6e6e73' }}>{customer.line2}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#8a8a92' }}>Terms</div>
          <div style={{ fontSize: 13, fontWeight: 600, marginTop: 3 }}>{due}</div>
        </div>
      </div>

      {/* Line items */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 14 }}>
        <thead>
          <tr>
            <th style={th}>Description</th>
            <th style={{ ...th, textAlign: 'center', width: 36 }}>Qty</th>
            <th style={{ ...th, textAlign: 'right', width: 64 }}>Rate</th>
            <th style={{ ...th, textAlign: 'right', width: 70 }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={i}>
              <td style={td}>{it.desc}</td>
              <td style={{ ...td, textAlign: 'center' }}>{it.qty}</td>
              <td style={{ ...td, textAlign: 'right' }}>{money(it.rate)}</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{money((Number(it.qty) || 0) * (Number(it.rate) || 0))}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      <div style={{ marginTop: 12, marginLeft: 'auto', width: 200 }}>
        <Row label="Subtotal" value={money(subtotal)} />
        {taxRate > 0 && <Row label={`Tax (${Math.round(taxRate * 100)}%)`} value={money(tax)} />}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0 0', marginTop: 6, borderTop: '2px solid #1d1d1f' }}>
          <span style={{ fontSize: 14, fontWeight: 800 }}>Total due</span>
          <span style={{ fontSize: 16, fontWeight: 800 }}>{money(total)}</span>
        </div>
      </div>

      {/* Pay action (visual) + footer */}
      <div style={{ marginTop: 14, textAlign: 'center', fontSize: 14, fontWeight: 700, color: '#fff', background: 'linear-gradient(160deg,#0a84ff,#0a5fd0)', borderRadius: 11, padding: '11px 14px' }}>
        Pay {money(total)} →
      </div>
      <div style={{ marginTop: 10, textAlign: 'center', fontSize: 11, color: '#8a8a92' }}>Thanks for your business — {business.email}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: '#3a3a40', padding: '3px 0' }}>
      <span>{label}</span>
      <span style={{ fontWeight: 600 }}>{value}</span>
    </div>
  );
}
